import { createContext, type ReactNode, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useCompany } from '@/contexts/CompanyContext';
import type { AdmAuthenticateResponse, AdmDataLink, AdmDocumentContext, AdmInstance } from '@/types/adm-document';
import { useAuth } from './AuthProvider';

interface DocumentApiContextValue {
  documentApiUrl: string | undefined;
  getDocumentContext: () => Promise<AdmDocumentContext | null>;
  isInitialized: boolean;
  isInitializing: boolean;
  error: string | null;
}

const DocumentApiContext = createContext<DocumentApiContextValue | null>(null);

export function useDocumentApi(): DocumentApiContextValue {
  const context = useContext(DocumentApiContext);
  if (!context) {
    throw new Error('useDocumentApi must be used within a DocumentApiProvider');
  }
  return context;
}

interface DocumentApiProviderProps {
  documentApiUrl: string | undefined;
  children: ReactNode;
}

interface InstanceDataLinks {
  instance: AdmInstance;
  dataLinks: AdmDataLink[];
}

export function DocumentApiProvider({ documentApiUrl, children }: DocumentApiProviderProps): React.ReactElement {
  const { getAccessToken } = useAuth();
  const { selectedCompany } = useCompany();
  const [isInitialized, setIsInitialized] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Cache discovered instances+dataLinks so we don't re-fetch on company switch
  const instanceCacheRef = useRef<InstanceDataLinks[] | null>(null);
  // Cache resolved context per dbName
  const contextCacheRef = useRef<Map<string, AdmDocumentContext>>(new Map());

  const getAuthHeaders = useCallback(async (): Promise<Record<string, string>> => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (getAccessToken) {
      const token = await getAccessToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }

    return headers;
  }, [getAccessToken]);

  const discoverAllInstances = useCallback(async (): Promise<InstanceDataLinks[]> => {
    if (instanceCacheRef.current) {
      console.log('[ADM] discoverAllInstances — returning cached', instanceCacheRef.current.length, 'instances');
      return instanceCacheRef.current;
    }

    if (!documentApiUrl) {
      console.error('[ADM] discoverAllInstances — documentApiUrl not configured');
      throw new Error('Document API URL not configured');
    }

    console.log('[ADM] discoverAllInstances — calling AuthenticateAccessToken at', documentApiUrl);
    const headers = await getAuthHeaders();

    const authResponse = await fetch(`${documentApiUrl}/Security/AuthenticateAccessToken`, {
      method: 'GET',
      headers,
    });

    if (!authResponse.ok) {
      console.error('[ADM] AuthenticateAccessToken failed', authResponse.status, authResponse.statusText);
      throw new Error(`ADM authentication failed (HTTP ${authResponse.status})`);
    }

    const authData: AdmAuthenticateResponse = await authResponse.json();
    console.log(
      '[ADM] AuthenticateAccessToken returned',
      authData.Instances.length,
      'instances:',
      authData.Instances.map((i) => ({ id: i.Id, url: i.Url })),
    );

    if (authData.Instances.length === 0) {
      throw new Error('No ADM instances found for this user');
    }

    // Fetch DataLinks for each instance in parallel
    const results: InstanceDataLinks[] = await Promise.all(
      authData.Instances.map(async (instance) => {
        console.log('[ADM] Fetching DataLinks for instance', instance.Id, 'at', instance.Url);
        const dataLinksResponse = await fetch(`${instance.Url}/DataLinks`, {
          method: 'GET',
          headers,
        });

        if (!dataLinksResponse.ok) {
          console.warn('[ADM] DataLinks failed for instance', instance.Id, dataLinksResponse.status);
          return { instance, dataLinks: [] };
        }

        const dataLinks: AdmDataLink[] = await dataLinksResponse.json();
        console.log(
          '[ADM] DataLinks for instance',
          instance.Id,
          ':',
          dataLinks.map((dl) => ({ name: dl.Name, dbName: dl.DatabaseName })),
        );
        return { instance, dataLinks };
      }),
    );

    instanceCacheRef.current = results;
    console.log('[ADM] discoverAllInstances — cached', results.length, 'instance results');
    return results;
  }, [documentApiUrl, getAuthHeaders]);

  const resolveContextForCompany = useCallback(
    async (companyDbName: string | undefined): Promise<AdmDocumentContext | null> => {
      console.log('[ADM] resolveContextForCompany — companyDbName:', companyDbName);
      if (companyDbName && contextCacheRef.current.has(companyDbName)) {
        console.log('[ADM] resolveContextForCompany — returning cached context for', companyDbName);
        return contextCacheRef.current.get(companyDbName)!;
      }

      const allInstances = await discoverAllInstances();

      // Match company's stored database name against ADM DataLinks
      if (companyDbName) {
        for (const { instance, dataLinks } of allInstances) {
          const match = dataLinks.find((dl) => dl.DatabaseName === companyDbName || dl.Name === companyDbName);
          if (match) {
            console.log(
              '[ADM] resolveContextForCompany — matched',
              companyDbName,
              'to instance',
              instance.Id,
              'dbName:',
              match.DatabaseName,
            );
            const ctx: AdmDocumentContext = {
              instanceUrl: instance.Url,
              dbName: match.DatabaseName,
            };
            contextCacheRef.current.set(companyDbName, ctx);
            return ctx;
          }
        }
        console.warn(
          '[ADM] resolveContextForCompany — no match found for',
          companyDbName,
          'across',
          allInstances.length,
          'instances',
        );
      }

      // Check if no instances have any DataLinks at all
      const withDataLinks = allInstances.filter((r) => r.dataLinks.length > 0);
      if (withDataLinks.length === 0) {
        console.warn('[ADM] resolveContextForCompany — no instances have DataLinks');
        toast.warning('No integration link found in Document system for this instance');
        return null;
      }

      // Fallback: if only one instance with one DataLink, use it
      if (withDataLinks.length === 1 && withDataLinks[0].dataLinks.length === 1) {
        const { instance, dataLinks } = withDataLinks[0];
        console.log(
          '[ADM] resolveContextForCompany — fallback: single instance',
          instance.Id,
          'single dataLink',
          dataLinks[0].DatabaseName,
        );
        const ctx: AdmDocumentContext = {
          instanceUrl: instance.Url,
          dbName: dataLinks[0].DatabaseName,
        };
        if (companyDbName) {
          contextCacheRef.current.set(companyDbName, ctx);
        }
        return ctx;
      }

      console.error(
        '[ADM] resolveContextForCompany — FAILED. companyDbName:',
        companyDbName,
        'instances with dataLinks:',
        withDataLinks.length,
      );
      throw new Error(
        companyDbName
          ? `No ADM instance found matching database "${companyDbName}"`
          : 'Cannot resolve ADM instance — company has no linked database name',
      );
    },
    [discoverAllInstances],
  );

  const getDocumentContext = useCallback(async (): Promise<AdmDocumentContext | null> => {
    const companyDbName = selectedCompany?.linkedDatabaseName;
    console.log(
      '[ADM] getDocumentContext — selectedCompany:',
      selectedCompany?.id,
      selectedCompany?.name,
      'linkedDatabaseName:',
      companyDbName,
    );

    if (!companyDbName) {
      console.warn('[ADM] getDocumentContext — company does not have a linked database name');
      toast.warning('Company does not have a linked database name configured');
      return null;
    }

    if (contextCacheRef.current.has(companyDbName)) {
      console.log('[ADM] getDocumentContext — returning cached context for', companyDbName);
      return contextCacheRef.current.get(companyDbName)!;
    }

    setIsInitializing(true);
    setError(null);

    try {
      const ctx = await resolveContextForCompany(companyDbName);
      setIsInitialized(true);
      console.log('[ADM] getDocumentContext — resolved:', ctx);
      return ctx;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to initialize ADM';
      setError(message);
      console.error('[ADM] getDocumentContext — FAILED:', err);
      return null;
    } finally {
      setIsInitializing(false);
    }
  }, [selectedCompany?.linkedDatabaseName, resolveContextForCompany]);

  const value = useMemo<DocumentApiContextValue>(
    () => ({
      documentApiUrl,
      getDocumentContext,
      isInitialized,
      isInitializing,
      error,
    }),
    [documentApiUrl, getDocumentContext, isInitialized, isInitializing, error],
  );

  return <DocumentApiContext.Provider value={value}>{children}</DocumentApiContext.Provider>;
}
